
import Vue from "vue";
import { Component, Watch } from "vue-property-decorator";
import SingularityPanelHeader from "../PanelHeader/SingularityPanelHeader.vue";
import { replaceImagesInSectionWithAi } from "@/futurelayer/images/main";
import { aiJobController } from "@/futurelayer/ai-job-manager/ai-jobs";
import { rewriteContentInSection } from "@/lib/singularity/sections";
import { documentStore, singularityStore } from "@/store/store";
import { findNode } from "@/lib/tree/tree";
import { updateSectionDescription, PageSection } from "@/lib/singularity/page-structure/page-structure";
import NiceButton from "@/components/UI/NiceButton.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import IconButton from "@/components/UI/IconButton.vue";
import Icon from "@/components/UI/Icon.vue";
import SingularityOption from "../components/SingularityOption.vue";
import { logEvent } from "@/util/tracking";

@Component({
  components: {
    SingularityPanelHeader,
    SingularityOption,
    NiceButton,
    TextInput,
    IconButton,
    Icon
  }
})
export default class RewriteSectionPanel extends Vue {
  option: "content" | "images" | null = null;

  closeDialog() {
    this.$emit("close");
  }

  closePanel() {
    singularityStore.setSectionRewritePanelState(null);
    singularityStore.setSingularityLeftPanel("page_structure");
  }

  setOption(option: "content" | "images") {
    this.option = option;
    logEvent("FutureLayer Section Rewrite Option Selected", {
      option
    });
  }

  internalSectionDescription = "";
  internalImagesDescription = "";

  get modalTitle() {
    if (this.option === "content") return "Edit with AI";
    if (this.option === "images") return "Images";
    return "Select";
  }

  get section() {
    if (!singularityStore.sectionRewritePanelState) return null;
    return singularityStore.sectionRewritePanelState.section;
  }

  rewrite() {
    if (!this.section) return;

    aiJobController.addJob(
      async abortController => {
        if (!this.section) return;
        await rewriteContentInSection(this.section.nodeId, abortController);
      },
      {
        jobType: "section_rewrite",
        documentId: documentStore.document.documentMeta.id,
        elementId: this.section.nodeId,
        description: `Section rewrite from rewrite panel for section ${this.section.nodeId}`,
        priority: 'critical',
        snapshotLabel: 'Rewrite section with AI'
      }
    );
    this.closeDialog();
    logEvent("FutureLayer Section Rewrite Started");
  }

  repopulateImages() {
    if (!this.section) return;
    const section = findNode(documentStore.document.tree, this.section.nodeId);
    if (!section) return;

    aiJobController.addJob(
      async abortController => {
        await replaceImagesInSectionWithAi(section, abortController);
      },
      {
        jobType: "section_image_replacement",
        documentId: documentStore.document.documentMeta.id,
        elementId: this.section.nodeId,
        description: `Image replacement from rewrite panel for section ${this.section.nodeId}`,
        priority: 'critical',
        snapshotLabel: 'Rewrite section with AI'
      }
    );
    this.closeDialog();
    logEvent("FutureLayer Section Image Replacement Started");
  }

  get isSectionCurrentlyRewritingWithAi() {
    if (!this.section) return false;
    return (
      singularityStore.aiJobs.filter(
        job =>
          "elementId" in job.context &&
          job.context.elementId === this.section?.nodeId &&
          (job.status === "running" || job.status === "pending") &&
          job.context.jobType === "section_rewrite"
      ).length > 0
    );
  }

  get isSectionCurrentlyReplacingImagesWithAi() {
    if (!this.section) return false;
    return (
      singularityStore.aiJobs.filter(
        job =>
          "elementId" in job.context &&
          job.context.elementId === this.section?.nodeId &&
          (job.status === "running" || job.status === "pending") &&
          job.context.jobType === "section_image_replacement"
      ).length > 0
    );
  }

  updateDescription() {
    if (!this.section) return;
    updateSectionDescription(
      this.section.nodeId,
      this.internalSectionDescription
    );
  }

  @Watch("section", { immediate: true })
  onSectionChange(newSection: PageSection | null) {
    this.internalSectionDescription = newSection?.description || "";
    this.internalImagesDescription = "";

    // For now, automatically select "content" option when panel opens, as the 
    // Replace Images functinoality isn't good yet, so we are hiding it
    if (newSection) {
      this.option = "content";
    } else {
      this.option = null;
    }
  }
}
